import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { firestore } from './firebase';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  deleteField,
} from 'firebase/firestore';
import './Menuwahl.css';
import { Modal, Button } from 'react-bootstrap';

const daysOfWeek = [
  { day: 'Montag', idx: 1, ger: 'Montag' },
  { day: 'Dienstag', idx: 2, ger: 'Dienstag' },
  { day: 'Mittwoch', idx: 3, ger: 'Mittwoch' },
  { day: 'Donnerstag', idx: 4, ger: 'Donnerstag' },
  { day: 'Freitag', idx: 5, ger: 'Freitag' },
];

const dayKeys = daysOfWeek.map((d) => d.day);

const getWeekNumber = (date) => {
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = (date - start) / 86400000;
  return Math.ceil((diff + start.getDay() + 1) / 7);
};

const Menuwahl = () => {
  const navigate = useNavigate();
  const db = firestore;

  const [menuChoices, setMenuChoices] = useState(() =>
    dayKeys.reduce(
      (acc, day) => ({
        ...acc,
        [day]: { portion: false, remark: '', selectedOption: '' },
      }),
      {}
    )
  );

  const [orderModal, setOrderModal] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [guestModal, setGuestModal] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeUser, setRemoveUser] = useState('');
  const [removeKey, setRemoveKey] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const now = new Date();

  const fetchMenuChoices = useCallback(async () => {
    const currentUserName = localStorage.getItem('workerLoginName');
    if (!currentUserName) return;

    const currentDate = new Date();
    const weekNumber = getWeekNumber(currentDate);
    const year = currentDate.getFullYear();
    const path = `Week-${weekNumber}-${year}`;

    const fresh = {};

    for (const day of dayKeys) {
      try {
        const snap = await getDoc(doc(db, path, day));
        if (!snap.exists() || !snap.data()[currentUserName]) {
          fresh[day] = { portion: false, remark: '', selectedOption: '' };
          continue;
        }

        const [portionText, ...remarkParts] = snap
          .data()[currentUserName]
          .split(' | ');
        const isHalf = portionText.startsWith('halbe portion');
        const option = isHalf
          ? portionText.replace('halbe portion ', '')
          : portionText;
        const remark = remarkParts.join(' | ') || '';

        fresh[day] = { portion: isHalf, selectedOption: option, remark };
      } catch (err) {
        console.error(`Error fetching ${day}:`, err);
      }
    }

    setMenuChoices(fresh);
  }, [db]);

  useEffect(() => {
    const currentUserName = localStorage.getItem('workerLoginName');

    if (!currentUserName) {
      navigate('/');
      return;
    }
    async function fetchAdminStatus() {
      try {
        const snap = await getDoc(doc(db, 'users', currentUserName));
        setIsAdmin(snap.exists() && snap.data().Admin === true);
      } catch (err) {
        console.error('Error fetching admin status', err);
      }
    }

    fetchAdminStatus();
    fetchMenuChoices();
  }, [navigate, fetchMenuChoices, db]);

  const persistChoice = async (day, choice, portion, remark) => {
    const currentUserName = localStorage.getItem('workerLoginName');
    if (!currentUserName) return;

    const today = new Date();
    const week = getWeekNumber(today);
    const year = today.getFullYear();
    const path = `Week-${week}-${year}`;

    const docRef = doc(db, path, day);

    if (!choice) {
      await setDoc(docRef, { [currentUserName]: deleteField() }, { merge: true });
      return;
    }

    const text = `${portion ? 'halbe portion ' : ''}${choice}${remark ? ' | ' + remark : ''
      }`;

    await setDoc(docRef, { [currentUserName]: text }, { merge: true });
  };

  const isLocked = (dayIndex) => {
    const now = new Date();
    const todayIdx = now.getDay();
    if (dayIndex < todayIdx) return true;
    if (dayIndex > todayIdx) return false;

    return now.getHours() > 9 || (now.getHours() === 9 && now.getMinutes() > 30);
  };

  const isLockedForDay = (day) => {
    const dayObj = daysOfWeek.find(d => d.day === day);
    if (!dayObj) return true;
    return isLocked(dayObj.idx);
  };

  const isGuest = (username) => {
    const firstChar = username.charAt(0);
    return firstChar === firstChar.toUpperCase() && firstChar !== firstChar.toLowerCase();
  };

  const updateField = (day, field, value) => {
    setMenuChoices((prev) => {
      const dayData = { ...prev[day] };

      if (field === 'selectedOption' && value === 'Nichts') {
        dayData.selectedOption = '';
        dayData.portion = false;
        dayData.remark = '';
      } else {
        dayData[field] = value;
      }

      const updated = { ...prev, [day]: dayData };
      const { selectedOption, portion, remark } = dayData;
      persistChoice(day, selectedOption, portion, remark);
      return updated;
    });
  };

  const updateGuestOrder = async (id, newFoodItem, newRemark = null) => {
    try {
      const [user, indexStr] = id.split('___');
      const index = parseInt(indexStr, 10);
      
      const today = new Date();
      const week = getWeekNumber(today);
      const year = today.getFullYear();
      const path = `Week-${week}-${year}`;
      
      const docRef = doc(db, path, selectedDay);
      const snap = await getDoc(docRef);
      
      if (!snap.exists() || !snap.data()[user]) {
        console.error('User order not found');
        return;
      }
      
      const userData = snap.data()[user];
      
      if (Array.isArray(userData)) {
        const updated = [...userData];
        // Parse existing order
        const [portionText, ...remarkParts] = updated[index].split(' | ');
        const isHalf = portionText.startsWith('halbe portion');
        const currentRemark = remarkParts.join(' | ') || '';
        
        // Use provided values or keep existing ones
        const finalFoodItem = newFoodItem !== null ? newFoodItem : (isHalf ? portionText.replace('halbe portion ', '') : portionText);
        const finalRemark = newRemark !== null ? newRemark : currentRemark;
        
        // Create new order string
        const newOrder = `${isHalf ? 'halbe portion ' : ''}${finalFoodItem}${finalRemark ? ' | ' + finalRemark : ''}`;
        updated[index] = newOrder;
        
        await setDoc(docRef, { [user]: updated }, { merge: true });
      } else {
        // Single order case
        const [portionText, ...remarkParts] = userData.split(' | ');
        const isHalf = portionText.startsWith('halbe portion');
        const currentRemark = remarkParts.join(' | ') || '';
        
        // Use provided values or keep existing ones
        const finalFoodItem = newFoodItem !== null ? newFoodItem : (isHalf ? portionText.replace('halbe portion ', '') : portionText);
        const finalRemark = newRemark !== null ? newRemark : currentRemark;
        
        const newOrder = `${isHalf ? 'halbe portion ' : ''}${finalFoodItem}${finalRemark ? ' | ' + finalRemark : ''}`;
        await setDoc(docRef, { [user]: newOrder }, { merge: true });
      }
      
      // Update local state
      setOrderData(prev => prev.map(order => 
        order.id === id ? { 
          ...order, 
          ...(newFoodItem !== null && { foodItem: newFoodItem }),
          ...(newRemark !== null && { remark: newRemark })
        } : order
      ));
    } catch (error) {
      console.error('Error updating guest order:', error);
    }
  };

  return (
    <div className="menuwahl-container fade-in">
      <div className="menu-selection">
        <h2>Menüwahl</h2>

        {daysOfWeek.map(({ day, idx, ger }) => (
          <div key={day}>
            <h3>{ger}</h3>

            <select
              value={menuChoices[day].selectedOption}
              disabled={isLocked(idx)}
              onChange={(e) =>
                updateField(day, 'selectedOption', e.target.value)
              }
            >
              <option value="Nichts">---</option>
              <option value="Tagesmenü">Tagesmenü</option>
              <option value="Vegetarisch">Vegetarisch</option>
              {/* <option value="Wurstkäsesalat">Wurstkäsesalat</option> */}
              <option value="Melonen Rohschinken">Melonen Rohschinken</option>
              {/* <option value="Tomaten Mozzarella Salat">Tomaten Mozzarella Salat</option> */}
              <option value="Schnitzel FR">Schnitzel (Französische Sauce)</option>
              <option value="Schnitzel IT">Schnitzel (Italienische Sauce)</option>
              <option value="Poulet FR">Poulet (Französische Sauce)</option>
              <option value="Poulet IT">Poulet (Italienische Sauce)</option>
            </select>

            <label>
              <input
                type="checkbox"
                disabled={isLocked(idx)}
                checked={menuChoices[day].portion}
                onChange={() =>
                  updateField(day, 'portion', !menuChoices[day].portion)
                }
              />
              Halbe Portion
            </label>

            <input
              type="text"
              placeholder="Bemerkung"
              disabled={isLocked(idx)}
              value={menuChoices[day].remark}
              onChange={(e) => updateField(day, 'remark', e.target.value)}
            />

            <button
              className="list-button"
              onClick={() => handleShowOrderList(day)}
            >
              Liste
            </button>
          </div>
        ))}

        <Modal
          show={orderModal}
          onHide={handleCloseOrderModal}
          centered
          dialogClassName="order-modal"
        >
          <Modal.Header>
            <Modal.Title>
              {selectedDay} Bestellungen ({orderData.length})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {orderData.length ? (
              <div className="order-table-container">
                <table className="order-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Halbe Portion</th>
                      <th>Essen</th>
                      <th>Bemerkung</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderData.map(
                      ({ id, user, foodItem, isHalfPortion, remark }) => {
                        const [username] = id.split('___');
                        const canEdit = isGuest(username) && !isLocked(daysOfWeek.find(d => d.day === selectedDay)?.idx || 0);
                        
                        return (
                          <tr key={id}>
                            <td>{user}</td>
                            <td>
                              <input type="checkbox" readOnly checked={isHalfPortion} />
                            </td>
                            <td>
                              {canEdit ? (
                                <select
                                  value={foodItem}
                                  onChange={(e) => updateGuestOrder(id, e.target.value, null)}
                                  className="guest-food-select"
                                >
                                  <option value="Tagesmenü">Tagesmenü</option>
                                  <option value="Vegetarisch">Vegetarisch</option>
                                  {/* <option value="Wurstkäsesalat">Wurstkäsesalat</option> */}
                                  <option value="Melonen Rohschinken">Melonen Rohschinken</option>
                                  {/* <option value="Tomaten Mozzarella Salat">Tomaten Mozzarella Salat</option> */}
                                  <option value="Schnitzel FR">Schnitzel (Französische Sauce)</option>
                                  <option value="Schnitzel IT">Schnitzel (Italienische Sauce)</option>
                                  <option value="Poulet FR">Poulet (Französische Sauce)</option>
                                  <option value="Poulet IT">Poulet (Italienische Sauce)</option>
                                </select>
                              ) : (
                                foodItem
                              )}
                            </td>
                            <td>
                              {canEdit ? (
                                <input
                                  type="text"
                                  value={remark}
                                  onChange={(e) => updateGuestOrder(id, null, e.target.value)}
                                  placeholder="Bemerkung"
                                  className="guest-remark-input"
                                />
                              ) : (
                                <em style={{ color: 'red' }}>{remark}</em>
                              )}
                            </td>
                            <td>
                              {isAdmin && (
                                <span
                                  className="remove-icon"
                                  onClick={() => handleAskRemove(id, user)}
                                >
                                  &times;
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Keine Bestellungen für diesen Tag.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            {(isAdmin || !isLockedForDay(selectedDay)) && <Button variant="primary" onClick={handleShowAddGuest}>
              Person Hinzufügen
            </Button>}
            <Button variant="secondary" onClick={handleCloseOrderModal}>
              Schliessen
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={confirmRemove}
          onHide={handleCancelRemove}
          centered
          dialogClassName="remove-modal"
        >
          <Modal.Header>
            <Modal.Title>{`${removeUser} entfernen?`}</Modal.Title>
          </Modal.Header>
          <Modal.Footer>
            <Button variant="primary" onClick={handleConfirmRemove}>
              Ja
            </Button>
            <Button variant="secondary" onClick={handleCancelRemove}>
              Nein
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={guestModal}
          onHide={handleCloseGuestModal}
          centered
          dialogClassName="guest-modal"
        >
          <Modal.Header>
            <Modal.Title>Person Hinzufügen</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {guestList.length ? (
              <select
                className="form-select guest-select"
                value={selectedGuest}
                onChange={(e) => setSelectedGuest(e.target.value)}
              >
                <option value=""></option>
                {guestList.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            ) : (
              <p>Keine Gäste gefunden.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={handleAddGuest}
              disabled={!selectedGuest}
            >
              Hinzufügen
            </Button>
            <Button variant="secondary" onClick={handleCloseGuestModal}>
              Abbrechen
            </Button>

          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );

  async function handleShowOrderList(day) {
    const today = new Date();
    const week = getWeekNumber(today);
    const year = today.getFullYear();
    const path = `Week-${week}-${year}`;

    try {
      const snap = await getDoc(doc(db, path, day));
      const data = snap.exists() ? snap.data() : {};

      const nameSnap = await getDocs(collection(db, 'users'));
      const userNames = {};
      nameSnap.forEach((d) => {
        const info = d.data();
        if (info.Name) userNames[d.id] = info.Name;
      });
      const orders = Object.entries(data)
        .flatMap(([userKey, value]) => {
          const display = userNames[userKey] || userKey;
          const buildEntry = (text, idx) => {
            const [portionText, ...remarkParts] = text.split(' | ');
            const isHalf = portionText.startsWith('halbe portion');
            const food = isHalf
              ? portionText.replace('halbe portion ', '')
              : portionText;
            const remark = remarkParts.join(' | ') || '';
            return {
              id: `${userKey}___${idx}`,
              user: display,
              foodItem: food,
              isHalfPortion: isHalf,
              remark,
            };
          };

          return Array.isArray(value)
            ? value.map((t, i) => buildEntry(t, i))
            : [buildEntry(value, 0)];
        })
        .sort((a, b) => a.foodItem.localeCompare(b.foodItem));

      setOrderData(orders);
      setSelectedDay(day);
      setOrderModal(true);
    } catch (err) {
      console.error(`Orders fetch error (${day})`, err);
    }
  }
  async function handleShowAddGuest() {
    try {
      const snap = await getDocs(collection(db, 'users'));
      const guests = [];
      snap.forEach((d) => {
        const name = d.id;
        if (isAdmin) {
          const firstChar = name.charAt(0);
          if (firstChar === firstChar.toUpperCase() && firstChar !== firstChar.toLowerCase()) {
            guests.push(name);
          }
        } else {
          if (name.startsWith("Gast")) {
            guests.push(name);
          }
        }
      });
      setGuestList(guests);
      setSelectedGuest('');
      setOrderModal(false);
      setGuestModal(true);
    } catch (err) {
      console.error('Error fetching guests', err);
    }
  }

  async function handleAddGuest() {
    if (!selectedGuest) return;

    const today = new Date();
    const week = getWeekNumber(today);
    const year = today.getFullYear();
    const path = `Week-${week}-${year}`;

    const docRef = doc(db, path, selectedDay);
    const snap = await getDoc(docRef);
    let existing = [];
    if (snap.exists() && snap.data()[selectedGuest]) {
      const current = snap.data()[selectedGuest];
      existing = Array.isArray(current) ? current : [current];
    }

    const updated = [...existing, 'Tagesmenü'];
    await setDoc(docRef, { [selectedGuest]: updated }, { merge: true });

    const newIndex = updated.length - 1;

    const userDoc = await getDoc(doc(db, 'users', selectedGuest));
    const display = userDoc.exists() && userDoc.data().Name
      ? userDoc.data().Name
      : selectedGuest;
    const newEntry = {
      id: `${selectedGuest}___${newIndex}`,
      user: display,
      foodItem: 'Tagesmenü',
      isHalfPortion: false,
      remark: '',
    };
    setOrderData((prev) =>
      [...prev, newEntry].sort((a, b) => a.foodItem.localeCompare(b.foodItem))
    );

    setGuestModal(false);
    setOrderModal(true);
    setSelectedGuest('');
  }
  function handleAskRemove(id, user) {
    if (!isAdmin) return;
    setRemoveKey(id);
    setRemoveUser(user);
    setOrderModal(false);
    setConfirmRemove(true);
  }

  async function handleConfirmRemove() {
    if (!isAdmin) return;
    const today = new Date();
    const week = getWeekNumber(today);
    const year = today.getFullYear();
    const path = `Week-${week}-${year}`;

    const [user, indexStr] = removeKey.split('___');
    const index = parseInt(indexStr, 10);

    const docRef = doc(db, path, selectedDay);
    const snap = await getDoc(docRef);
    const userData = snap.data()[user];

    if (Array.isArray(userData)) {
      const updated = [...userData];
      updated.splice(index, 1);
      const update = updated.length ? { [user]: updated } : { [user]: deleteField() };
      await setDoc(docRef, update, { merge: true });
    } else {
      await setDoc(docRef, { [user]: deleteField() }, { merge: true });
    }

    setConfirmRemove(false);
    setRemoveKey('');
    setRemoveUser('');
    await handleShowOrderList(selectedDay);
  }

  function handleCancelRemove() {
    setConfirmRemove(false);
    setRemoveKey('');
    setRemoveUser('');
    setOrderModal(true);
  }

  function handleCloseGuestModal() {
    setGuestModal(false);
    setOrderModal(true);
  }
  function handleCloseOrderModal() {
    setOrderModal(false);
    fetchMenuChoices();
  }
};

export default Menuwahl;